import React, { useEffect } from 'react';
import { useOneTrustConsent } from 'hooks/useOneTrustConsent';
import { analyticsManager } from 'features/analytics';
import { debug } from 'utils/debug';
import {
  hasWildcardCookieName,
  removeAllRelatedCookies,
  removeCookie,
  sanitizeWildcardCookieName,
} from 'utils/handleCookies';
import {
  ONE_TRUST_COOKIE_CATEGORIES,
  oneTrustCookieConfiguration,
} from '../../../configs/oneTrustCookieConfigurations';

interface OneTrustProviderProps {
  brandId: string;
  children: React.ReactNode;
}

export const OneTrustProvider: React.FC<OneTrustProviderProps> = ({
  brandId,
  children,
}) => {
  const oneTrustConsent = useOneTrustConsent();

  const initAnalytics = async () => {
    try {
      await analyticsManager.setProviders(brandId);
      analyticsManager.initProviders();
    } catch (error) {
      debug('error', error);
    }
  };

  useEffect(() => {
    // We don't want to initialize analytics providers if the user has not
    // consented to performance scripts and if the brandId is undefined
    if (oneTrustConsent.performance && brandId) {
      analyticsManager.setOneTrustConsent(true);
      initAnalytics();
    } else {
      analyticsManager.setOneTrustConsent(false);
    }

    const categoryConsentMap: Record<ONE_TRUST_COOKIE_CATEGORIES, boolean> = {
      [ONE_TRUST_COOKIE_CATEGORIES.STRICTLY_NECESSARY]:
        oneTrustConsent.strictlyNecessary, // Always allowed
      [ONE_TRUST_COOKIE_CATEGORIES.PERFORMANCE]: oneTrustConsent.performance,
      [ONE_TRUST_COOKIE_CATEGORIES.FUNCTIONAL]: oneTrustConsent.functional,
      [ONE_TRUST_COOKIE_CATEGORIES.TARGETING]: oneTrustConsent.targeting,
      [ONE_TRUST_COOKIE_CATEGORIES.SOCIAL]: oneTrustConsent.social,
    };

    oneTrustCookieConfiguration.forEach((cookie) => {
      const hasConsent = categoryConsentMap[cookie.oneTrustCategory];

      if (hasConsent) return;

      cookie.cookieNamesToRemove.forEach((cookieName) => {
        if (hasWildcardCookieName(cookieName)) {
          const sanitizedCookieName = sanitizeWildcardCookieName(cookieName);
          removeAllRelatedCookies(sanitizedCookieName, cookie.targetDomains);
        } else {
          removeCookie(cookieName, cookie.targetDomains);
        }
      });
    });
  }, [oneTrustConsent, brandId]);

  return <>{children}</>;
};
